/**
 * `GET /eu` — quem é o usuário autenticado, e o que ele pode.
 *
 * 🔒 **É daqui que vem a autorização, não do token.** O ID token do Cognito carrega
 * um claim `cognito:groups` e lê-lo "funcionaria"; ainda assim está errado, porque
 * permissão é dado de aplicação, editável por um Administrador sem reemitir o token
 * de ninguém (§5.1). O adaptador Cognito devolve `grupos: []` / `clientes: []` de
 * propósito, e este módulo é o outro lado desse contrato.
 *
 * 🔒 Fica na **API Adm**, e é a única exceção ao "API Adm é coisa de Administrador":
 * quem responde "quem sou eu" é a Lambda `Gestão Administrativa`, que a página
 * `Refinada` do drawio desenha atrás da API Adm e que `Especificações.txt` define
 * como "responsável por popular e buscar dados nas tabelas do RDS adm" — usuários,
 * permissionamento e clientes são exatamente essas tabelas.
 *
 * ⚠️ Consequência ainda em aberto com a iOX (§10): **todo usuário autenticado chama a
 * API Adm no boot**, inclusive o Cliente. A separação dos dois gateways continua
 * valendo para as TELAS — o eslint e `boundaryDeGateways.test.ts` seguem armados
 * sobre `src/modules/**` — mas ela é sobre onde o dado mora, não sobre quem é admin.
 *
 * A resposta é validada com zod em vez de aceita na confiança. Um backend que
 * mudasse a forma silenciosamente produziria, sem isso, uma tela sem permissão
 * nenhuma e nenhuma pista do motivo.
 */
import { clienteSchema, grupoSchema, usuarioSchema } from '@/api/contracts/administracao'
import type { Cliente, Grupo } from '@/api/contracts/administracao'
import { apiAdm } from '@/api/clients/apiAdm'
import { ErroDeApi, ErroDeConfiguracao } from '@/api/clients/criarCliente'
import { z } from 'zod'

export const perfilSchema = z.object({
  usuario: usuarioSchema,
  /** Só os clientes da lista do próprio usuário — nunca o catálogo inteiro (§5.2). */
  clientes: z.array(clienteSchema),
  /** Os grupos do usuário, **com suas permissões** — é o que o resolver consome. */
  grupos: z.array(grupoSchema),
})

export type Perfil = z.infer<typeof perfilSchema>

export type { Cliente, Grupo }

/**
 * Erro de perfil que a tela sabe explicar.
 *
 * O 403 é o caso interessante e não é uma falha do sistema: a conta existe no pool e
 * autenticou, mas ninguém a cadastrou na Administração. Distinguir isso de "a API
 * caiu" é a diferença entre uma instrução acionável e um "algo deu errado".
 */
export class ErroDePerfil extends Error {
  readonly semCadastro: boolean

  /**
   * Se **repetir a mesma chamada** tem chance de dar certo.
   *
   * A tela usa isto para decidir se oferece "tentar de novo". Um botão que não pode
   * funcionar — porque falta cadastro, ou porque o `config.json` não tem o endereço —
   * empurra o usuário a repetir um gesto inútil e a concluir que a culpa é dele.
   */
  readonly podeTentarNovamente: boolean

  constructor(mensagem: string, semCadastro: boolean, podeTentarNovamente: boolean) {
    super(mensagem)
    this.name = 'ErroDePerfil'
    this.semCadastro = semCadastro
    this.podeTentarNovamente = podeTentarNovamente
  }
}

const CHAVE = '/eu'

export async function carregarPerfilDaApi(): Promise<Perfil> {
  try {
    const bruto = await apiAdm.get<unknown>(CHAVE)
    return perfilSchema.parse(bruto)
  } catch (erro) {
    // ⚠️ ANTES de `ErroDeApi`: `ErroDeConfiguracao` é subclasse dele, e a ordem
    // inversa faria o caso mais específico cair no genérico sem aviso nenhum.
    if (erro instanceof ErroDeConfiguracao) {
      // O usuário não tem o que fazer aqui, e repetir nunca vai funcionar — o
      // endereço do gateway não chegou no config.json publicado. Nomear o campo é o
      // que transforma minutos de investigação numa correção óbvia de deploy.
      throw new ErroDePerfil(
        `A aplicação está sem o endereço de \`${erro.chave}\`. É um problema de configuração do ambiente, não do seu acesso — avise o time da Lascasas.`,
        false,
        false,
      )
    }
    if (erro instanceof ErroDeApi && erro.status === 403) {
      throw new ErroDePerfil(
        'Seu acesso foi autenticado, mas ainda não há cadastro para ele. Peça a um administrador da iOX para liberar sua conta.',
        true,
        // Só um Administrador destrava. Repetir devolve o mesmo 403.
        false,
      )
    }
    if (erro instanceof ErroDeApi) {
      // Aqui sim: gateway fora do ar, rede oscilando, throttling. Repetir é a
      // resposta certa.
      throw new ErroDePerfil(
        'Não foi possível carregar suas permissões. Tente novamente em instantes.',
        false,
        true,
      )
    }
    // Inclui a falha de validação do zod: a resposta chegou e não tem a forma
    // combinada. Tratar como indisponibilidade é honesto — não dá para operar com ela.
    throw new ErroDePerfil(
      'As permissões recebidas não puderam ser interpretadas. Fale com o suporte da iOX.',
      false,
      // Uma resposta fora do contrato virá igual na próxima tentativa.
      false,
    )
  }
}
